// tributi-api.js — amm-v3 (A5, ondata 32): parte PURA della vista "Imposte
// dell'anno" (pagina 'tributi' — piano imposte ad ALBERO), testabile sotto node
// (contract test server/src/lib/amm-v3-tributi.test.js).
//
// PRIMO consumatore reale di TreeView nel cantiere amm (spike o27 §2.3):
// l'albero categoria → tributo → voce → occorrenze è GIÀ un albero lato server
// (GET /tributi/albero). Qui la funzione PURA buildTributiTree lo mappa nei nodi
// che TreeView cammina (tree-view-core.flattenTree); espansioni e visibilità
// restano del CHIAMANTE (la vista), come da contratto di TreeView.
//
// PRINCIPI (rito A1..A4/proc-v3):
//  - TESTI VERBATIM dal v1 (admin-module.js, renderTributi r.6663-6781):
//    il contract test pinna OGNI stringa di TR_TEXTS sul sorgente v1.
//  - FORMULE del v1 replicate come funzioni pure (pct avanzamento, voce
//    multi-periodo, forma dell'albero + regola del th tributo): stessi quirk.
//  - DELEGHE alle primitive v1 (mai reimplementate): editor del dovuto
//    (AMM.openDovuto, dove vivono guardClose/busy/setDovuto — resta V1),
//    "Perché non torna?" (AMM.openQuadraturaSpiegata), badge stato
//    (AMM.tribStatoBadge, HTML), etichetta periodo (AMM.tribPeriodoLabel),
//    fmtEur2, toast. admin-module.js resta OFF-LIMITS: zero byte cambiati lì.
//  - ZERO bypass di render: openDovuto riceve l'onSaved che GLI PASSA il v3
//    (refresh v3 via renderPage), mai renderTributi diretto ⇒ NIENTE interceptor.
//  - MAI 'nav:badge' o canali nuovi da client/src/amm-v3/**.

import { apiPathFn } from './api.js';

export const TR_DELEGATED_METHODS = [
  'renderPage',            // il seam stesso (wrap) + refresh post-azione
  'renderTributi',         // la vista v1 wrappata (drift guard: rename ⇒ rosso)
  'toast',                 // esito export fallito (stesso canale del v1)
  'fmtEur2',               // formattazione € (unica fonte: v1)
  'tribStatoBadge',        // badge stato voce/occorrenza (HTML v1, iniettato)
  'tribPeriodoLabel',      // etichetta periodo occorrenza (unica fonte: v1)
  'openDovuto',            // editor del dovuto (modale con setDovuto) — resta v1
  'openQuadraturaSpiegata', // box "Perché non torna?" — resta v1
];

export const TR_API_PATHS = [
  'tributi.albero',        // GET /tributi/albero?anno= (l'albero della vista)
  'export.download',       // Esporta CSV ('tributi', anno)
];

// ---- TESTI VERBATIM dal v1 (pinnati dal contract test) ----
export const TR_TEXTS = {
  title: "Imposte dell'anno",
  titleTooltip: "Tecnicamente 'Piano imposte'",
  perche: 'Perché non torna? ›',
  exportCsv: 'Esporta CSV',
  sub: 'anno di cassa · dovuto: calcolato dai cedolini dove possibile, sempre modificabile',
  kpiDovutoPre: 'Dovuto ',
  kpiPagato: 'Pagato',
  kpiResiduo: 'Residuo',
  kpiAvanzamento: 'Avanzamento',
  colTributoVoce: 'Tributo / voce',
  colDovuto: 'Dovuto',
  colPagato: 'Pagato',
  colResiduo: 'Residuo',
  imposta: 'imposta…',
  manuale: 'manuale',
  daClassificareNota: 'righe F24 senza mese di riferimento',
  exportFallito: 'Export fallito: ',
  dash: '—',
};

// colori KPI — VERBATIM v1 r.6727 (Pagato verde, Residuo rosso)
export const KPI_COLORS = { dovuto: '#0f172a', pagato: '#065f46', residuo: '#9f1239', avanzamento: '#0f172a' };

// percentuale di avanzamento (v1 r.6670): pagato/dovuto, cap 100, 0 se dovuto 0
export function pctAvanzamento(totali) {
  const t = totali || {};
  return t.dovuto ? Math.min(100, Math.round(t.pagato / t.dovuto * 100)) : 0;
}

// una voce è multi-periodo quando ha più di 1 occorrenza (v1 r.6662,
// tribVoceMulti): le annuali/rata restano a 1 occorrenza sola.
export function voceMulti(v) {
  return !!(v && v.occorrenze && v.occorrenze.length > 1);
}

// KPI del riquadro (v1 r.6726-6728): Dovuto <anno>, Pagato, Residuo, Avanzamento.
export function kpiDefs(albero, fmtEur2) {
  const t = (albero && albero.totali) || {};
  return [
    { label: TR_TEXTS.kpiDovutoPre + albero.anno, val: fmtEur2(t.dovuto), color: KPI_COLORS.dovuto },
    { label: TR_TEXTS.kpiPagato, val: fmtEur2(t.pagato), color: KPI_COLORS.pagato },
    { label: TR_TEXTS.kpiResiduo, val: fmtEur2(t.residuo), color: KPI_COLORS.residuo },
    { label: TR_TEXTS.kpiAvanzamento, val: pctAvanzamento(t) + '%', color: KPI_COLORS.avanzamento },
  ];
}

// ---- FORMA DELL'ALBERO per TreeView (walk depth-first) ----
// Mappa albero.categorie nei nodi che TreeView cammina, replicando ESATTAMENTE
// la struttura del v1 (renderTributi r.6690-6717):
//   cat  → children = [ (trib-head?)  +  voce...] appiattiti per ogni tributo
//   voce → children = occorrenze SOLO se multi-periodo (chevron)
// La riga "th" del tributo compare SOLO se il tributo ha >1 voce OPPURE la sua
// etichetta è diversa da quella dell'unica voce (v1 r.6697) — regola replicata
// così com'è (voci vuote ⇒ eccezione come nel v1: il server garantisce ≥1 voce).
export function buildTributiTree(categorie) {
  return (categorie || []).map((cat) => ({
    _key: 'cat:' + cat.categoria,
    kind: 'cat',
    cat,
    children: catChildren(cat),
  }));
}

function catChildren(cat) {
  const out = [];
  for (const t of cat.tributi || []) {
    if (t.voci.length > 1 || t.tributo !== t.voci[0].label) {
      out.push({ _key: 'trib:' + cat.categoria + ':' + t.tributo, kind: 'trib', trib: t });
    }
    for (const v of t.voci) {
      out.push({
        _key: 'voce:' + v.codice,
        kind: 'voce',
        voce: v,
        children: voceMulti(v)
          ? (v.occorrenze || []).map((o) => ({ _key: 'occ:' + v.codice + ':' + o.periodo, kind: 'occ', voce: v, occ: o }))
          : [],
      });
    }
  }
  return out;
}

// chiave di nodo per TreeView (nodeKey): il _key costruito sopra (mai undefined)
export function treeKey(node) { return (node && node._key) || ''; }

// ---- LETTURA dati (v1 r.6666): l'albero dell'anno ----
export async function readTributi(win, anno) {
  return apiPathFn(win, 'tributi.albero').call(null, anno);
}

// ---- AZIONI e DELEGHE (stessi punti del v1) ----
// Editor del dovuto (modale v1). Il v3 passa il PROPRIO onSaved (refresh v3):
// il modale non chiama mai renderTributi diretto (niente bypass/interceptor).
// periodo presente ⇒ si edita UNA occorrenza (v1 r.6778).
export function apriDovuto(win, anno, voce, onSaved, periodo) { win.AMM.openDovuto(anno, voce, onSaved, periodo); }
// Box "Perché non torna?" (v1 r.6736): quadratura spiegata dell'anno.
export function apriQuadratura(win, anno) { win.AMM.openQuadraturaSpiegata(anno); }
// Export CSV (v1 r.6741): vista 'tributi', anno visualizzato.
export function esportaCsv(win, anno) { return apiPathFn(win, 'export.download').call(null, 'tributi', anno); }
